import { API, AUTH_HEADER, ROUTES } from "@/api";
import store from "@/state/store";
import { isEnforcePendingAuthorizationEnabled } from "@/config/featureFlags";

export const GATED_ROUTE_NAMES = [
  "new-leave-application",
  "leave-application",
  "select-timesheet-period",
  "timesheet",
  "timesheet-entry",
  "new-travel-request",
  "travel-requests",
];

function isGatedUserType(userType) {
  const type = parseInt(userType, 10);
  return type === 2 || type === 3;
}

export function shouldApplyPendingAuthorizationGate(route) {
  const userType = store.getters["auth/getUser"]?.user_type;
  if (!isGatedUserType(userType)) {
    return false;
  }
  return GATED_ROUTE_NAMES.includes(route.name);
}

export async function refreshEnforceAuthorizationSetting() {
  if (!store.getters["auth/loggedIn"]) {
    return null;
  }
  return store.dispatch("settings/fetchEnforceAuthorization");
}

function resolveEmployeeId(vm) {
  return vm?.getEmployee?.emp_id ?? store.getters["auth/getEmployee"]?.emp_id;
}

/** Matches leave authorization index: pending app + officer with auth_status 0. */
function countPendingLeaveAuthorizations(data, officers) {
  if (!Array.isArray(data) || !data.length) {
    return 0;
  }

  return data.filter((application) => {
    if (parseInt(application?.leapp_status, 10) !== 0) {
      return false;
    }

    if (!Array.isArray(officers) || !officers.length) {
      return true;
    }

    return officers.some(
      (officer) =>
        parseFloat(officer.auth_travelapp_id) ===
          parseFloat(application.leapp_id) &&
        parseInt(officer.auth_status, 10) === 0
    );
  }).length;
}

/**
 * Blocks service-form entry when the employee has pending supervisor authorizations.
 * Returns true if a redirect was triggered.
 */
export async function enforcePendingAuthorizationGate(vm) {
  await refreshEnforceAuthorizationSetting();

  if (!isEnforcePendingAuthorizationEnabled()) {
    return false;
  }

  const empId = resolveEmployeeId(vm);
  if (!empId) {
    return false;
  }

  const results = await Promise.all([
    checkPendingLeaveAuthorization(empId),
    checkPendingTimesheetAuthorization(vm, empId),
    checkPendingTravelAuthorization(empId),
  ]);

  const pending = results.find((result) => result !== null);
  if (pending) {
    return pending;
  }

  return false;
}

const LEAVE_AUTH_REDIRECT = {
  message:
    "You have pending leave authorization. Kindly attend to them before proceeding.",
  redirect: { name: "leave-authorization" },
};

async function checkPendingLeaveAuthorization(empId) {
  const base = `${ROUTES.leaveApplication}/authorization/supervisor/${empId}`;

  try {
    const res = await API.get(`${base}/0`, { headers: AUTH_HEADER() });
    const { data, officers } = res.data || {};
    if (countPendingLeaveAuthorizations(data, officers) > 0) {
      return LEAVE_AUTH_REDIRECT;
    }
    return null;
  } catch {
    // fall through to legacy endpoint only when /0 is unavailable
  }

  try {
    const res = await API.get(base, { headers: AUTH_HEADER() });
    const { data, officers } = res.data || {};
    if (countPendingLeaveAuthorizations(data, officers) > 0) {
      return LEAVE_AUTH_REDIRECT;
    }
  } catch {
    return null;
  }

  return null;
}

async function checkPendingTimesheetAuthorization(vm, empId) {
  try {
    const url = `${ROUTES.timeAllocation}/authorization/${empId}`;
    const res = vm?.apiGet
      ? await vm.apiGet(url, "Get Authorizations Error")
      : await API.get(url, { headers: AUTH_HEADER() });
    const { officers } = res.data || {};
    if (!officers?.length) {
      return null;
    }

    const pendingCount = officers.filter((off) => {
      const timeStatus = parseInt(off?.auth_status || 0, 10);
      const type = parseInt(off?.auth_type || 0, 10);
      const authOfficer = parseInt(off?.auth_officer_id || 0, 10);
      return (
        timeStatus === 0 && type === 2 && parseInt(empId, 10) === authOfficer
      );
    }).length;

    if (pendingCount > 0) {
      return {
        message:
          "You have pending time sheet authorization. Kindly attend to them before proceeding.",
        redirect: { name: "time-sheet-authorization" },
      };
    }
    return null;
  } catch {
    return null;
  }
}

async function checkPendingTravelAuthorization(empId) {
  try {
    const url = `${ROUTES.travelApplication}/authorization/supervisor/${empId}`;
    const res = await API.get(url, { headers: AUTH_HEADER() });
    const { travelApplicationsForAuth, officers } = res.data || {};
    const supervisorId = parseInt(empId, 10);

    const pendingFromOfficers = (officers || []).filter(
      (off) =>
        parseInt(off?.auth_status, 10) === 0 &&
        parseInt(off?.auth_officer_id, 10) === supervisorId
    ).length;

    if (pendingFromOfficers > 0) {
      return {
        message:
          "You have pending travel authorization. Kindly attend to them before proceeding.",
        redirect: { name: "travel-authorization" },
      };
    }

    if (Array.isArray(travelApplicationsForAuth) && travelApplicationsForAuth.length) {
      const pendingApps = travelApplicationsForAuth.filter((app) =>
        (officers || []).some(
          (off) =>
            parseFloat(off.auth_travelapp_id) ===
              parseFloat(app.travelapp_id) &&
            parseInt(off.auth_status, 10) === 0 &&
            parseInt(off.auth_officer_id, 10) === supervisorId
        )
      );
      if (pendingApps.length > 0) {
        return {
          message:
            "You have pending travel authorization. Kindly attend to them before proceeding.",
          redirect: { name: "travel-authorization" },
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}
